<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Material..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.back()">
                    <i class="fa fa-arrow-left"></i> Materials
                </a>
                <span class="sep">/</span>
                <span class="current">{{ material.name || 'Material' }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-accent-light);color:var(--pp-accent);">
                    <img v-if="material.photo" :src="getFileUrl(material.photo)"
                        style="width:100%;height:100%;object-fit:cover;border-radius:12px;" alt="Material" />
                    <i v-else class="fa fa-box"></i>
                </div>
                <div class="profile-header-body">
                    <h3>{{ material.name || 'Unnamed Material' }} - ({{ material.type || ' ' }})</h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ material.external_id || 'N/A' }}</span>
                        <span v-if="material.code"><i class="fa fa-barcode"></i>{{ material.code }}</span>
                        <span v-if="material.manufacturer"><i class="fa fa-industry"></i>{{ material.manufacturer }}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge" :class="statusBadgeClass">
                        <span class="dot"></span> {{ statusLabel }}
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click="editMaterial(material)">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-weight-hanging"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Quantity</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ material.quantity || 0 }} {{ material.unit || '' }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-shopping-cart"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Total Ordered</div>
                        <div class="profile-stat-value">{{ totalOrdered }} {{ material.unit || '' }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-truck"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Vendors</div>
                        <div class="profile-stat-value">{{ vendors.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Created</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(material.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: Material details -->
                <div class="col-lg-8 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> Material Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">
                                <div class="profile-field" v-if="material.code">
                                    <span class="profile-label">Code</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ material.code }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.manufacturer">
                                    <span class="profile-label">Manufacturer</span>
                                    <span class="profile-value">{{ material.manufacturer }}</span>
                                </div>
                                <div class="profile-field" v-if="material.quantity">
                                    <span class="profile-label">Quantity</span>
                                    <span class="profile-value">{{ material.quantity }} {{ material.unit || '' }}</span>
                                </div>
                                <div class="profile-field" v-if="categoryName">
                                    <span class="profile-label">Category</span>
                                    <span class="profile-value">
                                        <span class="profile-badge c-accent">{{ categoryName }}</span>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.serial_number">
                                    <span class="profile-label">Serial Number</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ material.serial_number }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="material.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(material.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Description: full-width field -->
                            <div class="profile-field mt-3" v-if="material.description"
                                style="grid-column:1/-1;">
                                <span class="profile-label">Description</span>
                                <span class="profile-value" style="line-height:1.6;">
                                    {{ material.description }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Purchase Order History -->
                    <div class="profile-card" v-if="poItems.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-amber-text);">
                                <i class="fa fa-shopping-cart" style="color:var(--pp-amber-text);"></i> Purchase Order History
                            </div>
                            <span class="profile-count-pill">{{ poItems.length }}</span>
                        </div>
                        <div class="profile-card-body p-0">
                            <div class="table-responsive">
                                <table class="profile-table">
                                    <thead>
                                        <tr>
                                            <th>PO Number</th>
                                            <th>Date</th>
                                            <th>Vendor</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                            <th>Total</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, i) in poItems" :key="item.id || i">
                                            <td>
                                                <span class="profile-value" style="font-family:monospace;font-size:13px;">
                                                    {{ item.purchase_order?.external_id || '—' }}
                                                </span>
                                            </td>
                                            <td class="profile-value text-muted" style="font-size:13px;">
                                                {{ formatDate(item.purchase_order?.created_at) || '—' }}
                                            </td>
                                            <td>
                                                <span class="profile-value" style="font-size:13px;">
                                                    {{
                                                        item.purchase_order?.vendor?.get_profile?.company_name
                                                        || item.purchase_order?.vendor?.first_name
                                                        || '—'
                                                    }}
                                                </span>
                                            </td>
                                            <td>
                                                <span class="profile-badge c-info" style="font-size:12px;">
                                                    {{ item.quantity }} {{ item.unit }}
                                                </span>
                                            </td>
                                            <td class="profile-value" style="font-size:13px;">
                                                ${{ formatPrice(item.unit_price) }}
                                            </td>
                                            <td class="profile-value" style="font-size:13px;font-weight:600;">
                                                ${{ formatPrice(item.total_price) }}
                                            </td>
                                            <td>
                                                <span class="profile-badge" :class="poStatusBadgeClass(item.purchase_order?.status)">
                                                    {{ labelStatus(item.purchase_order?.status) }}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Attachment -->
                    <div class="profile-card" v-if="material.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-paperclip"></i> Document Attachment
                            </div>
                            <span class="profile-count-pill">1 file</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="row g-2">
                                <div class="col-6 col-sm-4 col-md-3">
                                    <div class="profile-attachment">
                                        <div class="profile-attachment-thumb">
                                            <i class="fa fa-file-pdf-o"
                                                style="font-size:24px;color:var(--pp-text-muted);"></i>
                                        </div>
                                        <div class="profile-attachment-meta">
                                            <div class="profile-attachment-name">Material File</div>
                                            <a :href="getFileUrl(material.file)" target="_blank"
                                                class="profile-attachment-size"
                                                style="color:var(--pp-accent);text-decoration:none;font-size:11px;">
                                                <i class="fa fa-download"></i> Download
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Connections sidebar -->
                <div class="col-lg-4 d-flex flex-column gap-3">

                    <!-- Vendors -->
                    <div class="profile-card" v-if="vendors.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-amber-text);">
                                <i class="fa fa-truck" style="color:var(--pp-amber-text);"></i> Vendors
                            </div>
                            <span class="profile-count-pill">{{ vendors.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <div v-for="(v, i) in vendors" :key="v.id || i"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-warning" style="width:28px;height:28px;font-size:11px;">
                                        {{ vendorName(v).charAt(0).toUpperCase() }}
                                    </div>
                                    <span class="profile-value" style="font-size:13px;">
                                        {{ vendorName(v) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Linked Assets -->
                    <div class="profile-card" v-if="assets.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-cubes" style="color:var(--pp-accent);"></i> Linked Assets
                            </div>
                            <span class="profile-count-pill">{{ assets.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <div v-for="(a, i) in assets" :key="a.id || i"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-accent" style="width:28px;height:28px;font-size:11px;">
                                        <i class="fa fa-microchip" style="font-size:12px;"></i>
                                    </div>
                                    <span class="profile-value" style="font-size:13px;">
                                        {{ a.name }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Empty connections state -->
                    <div class="profile-card"
                        v-if="!vendors.length && !assets.length && !material.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-plug"></i> Connections
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-empty">
                                <i class="fa fa-plug"></i>
                                <p>No connections yet</p>
                                <span style="font-size:12px;color:var(--pp-text-muted);">
                                    This material isn't linked to any vendors or assets
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>
     <FormModal v-if="showEditModal" ref="editModal" title="Edit Material" id="edit_material_form_modal">
        <EditMaterial :materialId="editingMaterialId" @created="onCreated" @close="closeEditModal" />
      </FormModal>

</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import EditMaterial from './EditMaterial.vue';

export default {
    name: 'MaterialProfile',
    components: { LoadingBar,
        FormModal,
        EditMaterial
     },

    data() {
        return {
            material: {},
            loading: false,
            external_id: this.$route.params.external_id,
                  editingMaterialId: null,
      showEditModal: false,
        }
    },

    computed: {
        categoryName() {
            return this.material.get_category?.name || ''
        },

        vendors() {
            return (this.material.get_vendors || []).filter(Boolean)
        },

        assets() {
            return (this.material.get_assets || [])
                .map(a => a.get_asset || a)
                .filter(Boolean)
        },

        poItems() {
            return this.material.get_po_items || []
        },

        totalOrdered() {
            return this.poItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
        },

        openPoCount() {
            return this.poItems.filter(
                item => item.purchase_order?.status === 'open' || item.purchase_order?.status === 'pending'
            ).length
        },
        statusLabel() {
            return this.material.status || 'Active'
        },

        statusBadgeClass() {
            const status = this.material.status?.toLowerCase() || 'active'
            const map = {
                'active': 'c-success',
                'inactive': 'c-danger',
                'pending': 'c-warning',
                'draft': 'c-info'
            }
            return map[status] || 'c-success'
        },

    },

    mounted() {
        this.fetchProfile()
    },

    methods: {
        formatDate(date) {
            if (!date) return '—'
            try {
                return new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            } catch {
                return date
            }
        },

        vendorName(v) {
            const first = v.first_name || ''
            const last = v.last_name || ''
            return (first + ' ' + last).trim() || v.get_profile?.company_name || 'Vendor'
        },

        formatPrice(val) {
            return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        labelStatus(status) {
            const map = { open: 'Open', pending: 'Pending', received: 'Received', closed: 'Closed' }
            return map[status] || status || '—'
        },
        poStatusBadgeClass(status) {
            const map = {
                'open': 'c-info',
                'pending': 'c-warning',
                'received': 'c-success',
                'closed': 'c-secondary'
            }
            return map[status] || 'c-secondary'
        },
        async fetchProfile() {
            this.loading = true
            const result = await form_api_submit({
                url: `materials/profile/${this.external_id}`,
                method: 'GET',
                data: {},
                show_success_toast: false,
                show_error_toast: true,
                remove_pre_toasts: false,
            })
            if (result.success) {
                this.material = result.data
            }
            this.loading = false
        },

        getFileUrl(filePath) {
            if (!filePath) return ''
            if (typeof filePath === 'string' && filePath.startsWith('http')) {
                return filePath
            }
            const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
            return `${baseUrl}/${filePath}`
        },
         closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },

    async onCreated() {
      await this.fetchProfile()
    },
         editMaterial(material) {
            console.log(material);
      this.editingMaterialId = material.id
      this.showEditModal = true
      this.$nextTick(() => {
        this.$refs.editModal.showModal()
      })
    },
    closeEditModal(ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].closeModal()
      }

      this.editingMaterialId = null
      this.showEditModal = false
    },
    },

}
</script>
